import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiService } from '../../shared/services/api.service';
import { DocumentService } from '../../shared/services/document.service';

@Component({
  selector: 'app-sms-redirect',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sms-redirect.component.html',
  styleUrls: ['./sms-redirect.component.scss'],
})
export class SmsRedirectComponent implements OnInit, OnDestroy {
  private sub: Subscription = new Subscription();
  private hasTriggered = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private documentService: DocumentService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.sub.add(
      this.route.queryParams.subscribe((queryParams) => {
        const queryId =
          queryParams['id'] ||
          queryParams['ID'] ||
          this.route.snapshot.paramMap.get('id');

        const trimmedId = (queryId || '').trim();

        if (!trimmedId) {
          this.attemptCloseOrRedirect();
          return;
        }

        if (this.hasTriggered) {
          return;
        }
        this.hasTriggered = true;

        this.apiService
          .smsinvoiceDownload({ short_code: trimmedId })
          .subscribe({
            next: (response: any) => {
              try {
                if (
                  response &&
                  (response.success === false ||
                    response.status === false ||
                    (typeof response.status === 'number' && response.status >= 400))
                ) {
                  this.attemptCloseOrRedirect();
                  return;
                }

                const extracted = this.documentService.extractPdfData(
                  response,
                  `invoice_${trimmedId}.pdf`,
                );

                if (extracted?.url || extracted?.base64) {
                  this.documentService.downloadPdfFile(extracted);
                }
              } catch (e) {
                console.error('Download execution error:', e);
              } finally {
                this.attemptCloseOrRedirect();
              }
            },
            error: (err: any) => {
              console.error('Invoice API error:', err);
              this.attemptCloseOrRedirect();
            },
          });
      }),
    );
  }

  /**
   * Attempts to close the tab. If the browser blocks window.close() (e.g. on desktop Chrome),
   * automatically redirects to /home so the user lands seamlessly on the Rewire portal.
   */
  attemptCloseOrRedirect(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // 1. Attempt programmatic window close
    this.closeTab();

    // 2. If the browser prevents closing (desktop Chrome), redirect to /home
    setTimeout(() => {
      this.closeTab();
      this.router.navigate(['/home']);
    }, 600);
  }

  /**
   * Tries multiple browser mechanisms to close the current tab.
   */
  closeTab(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      window.open('', '_self', '');
      window.close();
    } catch {}

    try {
      window.close();
    } catch {}

    try {
      if (window.opener) {
        window.close();
      }
    } catch {}

    try {
      (window as any).top?.close();
    } catch {}
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
